/**
 * RBACContext — Frontend Authorization Context (Phase 3)
 *
 * Reads permissions[] and role_assignments[] stored at login.
 * Provides permission checks, role helpers, and clinic context switching.
 * Supports dynamic refresh via GET /api/auth/unified/me.
 * This is a UX layer only — backend remains the source of truth.
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import api, { isSelfOwnedSessionRoute } from '../utils/api';
import {
  resolveUnambiguousWorkspace,
  readTrustedWorkspaceSelection,
  persistTrustedWorkspaceSelection,
  clearWorkspaceSelection,
} from '../utils/workspaceSelection';

const RBACContext = createContext(null);

const MULTI_CLINIC_ROLE_ID = 'provider_doctor';

const STORAGE_KEYS = {
  permissions: 'treatx_rbac_permissions',
  roleAssignments: 'treatx_rbac_role_assignments',
  activeClinic: 'treatx_active_clinic_id',
  // OF-001B — last active workspace persistence (FE-only; backend remains SSOT).
  workspaces: 'treatx_rbac_workspaces',
};

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

// PAYMENT-SETUP-LINK-01 — non-hook reader over the SAME canonical permission
// store the RBAC context itself loads (hydrated from the server at login).
// Lets a leaf component ask "may this user open that screen?" without a
// provider in its tree and without inventing a second permission list.
export const readStoredPermissions = () => loadFromStorage(STORAGE_KEYS.permissions, []);

export const storedPermissionGranted = (permission) =>
  readStoredPermissions().includes(permission);

// MTMR-P0 — provider-scoped client caches that MUST NOT survive a
// workspace switch (messaging, leads/patients, quotes/offers, tasks,
// financial/dashboard state). Authorization is server-side; this purge
// exists so stale Provider-A data cannot be rendered under Provider B.
const PROVIDER_SCOPED_CACHE_KEYS = [
  'repPatientConversations',
  'patientTimelines',
  'patientFlowState',
  'treatx_clinic_user',
  'treatx_pending_quote',
  'treatx_counter_INV',
  'treatx_counter_ORG',
  'treatx_counter_PAY',
  'treatx_counter_PRV',
  'treatx_counter_SUB',
  'treatx_counter_CRT',
  'treatx_counter_USR',
];

export function purgeProviderScopedState() {
  for (const key of PROVIDER_SCOPED_CACHE_KEYS) {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }
  }
}

// MTMR-P0-CLOSURE — minimum cross-tab synchronisation. The auth cookie
// (and therefore the active Provider authority) is browser-wide and
// globally replaced on switch; independent per-tab Provider authority is
// NOT created. BroadcastChannel is used when available with a
// `storage`-event fallback. The SERVER remains the security boundary:
// even if this signalling fails, every backend read/write still rejects
// the wrong Provider.
const CONTEXT_CHANNEL = 'treatx-active-context';
const CONTEXT_PING_KEY = 'treatx_context_ping';

export function notifyContextChanged(workspaceId) {
  const payload = { workspace_id: workspaceId || null, at: Date.now() };
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(CONTEXT_CHANNEL);
      ch.postMessage(payload);
      ch.close();
    }
  } catch { /* ignore */ }
  try {
    // Fallback + belt-and-braces: `storage` fires in OTHER tabs only.
    localStorage.setItem(CONTEXT_PING_KEY, JSON.stringify(payload));
  } catch { /* ignore */ }
}

export const RBACProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() => loadFromStorage(STORAGE_KEYS.permissions, []));
  const [roleAssignments, setRoleAssignments] = useState(() => loadFromStorage(STORAGE_KEYS.roleAssignments, []));
  const [activeClinicId, setActiveClinicIdRaw] = useState(() => sessionStorage.getItem(STORAGE_KEYS.activeClinic) || null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  // OF-001B — workspace catalogue + active workspace id (FE-only, derived
  // from canonical /me workspaces[] payload).
  const [workspaces, setWorkspaces] = useState(() => loadFromStorage(STORAGE_KEYS.workspaces, []));
  const [activeWorkspaceId, setActiveWorkspaceIdRaw] = useState(
    // TREATX-UAT-PROD-CONTEXT-SURFACE-01d — an unqualified legacy value is
    // NOT evidence of user intent; it is purged on first read.
    () => readTrustedWorkspaceSelection()
  );
  // MTMR-P0-CLOSURE — the SERVER-VALIDATED active context (from
  // /auth/unified/me or the context switch response). This — not the
  // union of role_assignments — drives Provider-scoped UI authority.
  const [activeContext, setActiveContext] = useState(null);
  const [contextRequired, setContextRequired] = useState(false);
  const refreshInFlight = useRef(false);

  // Derived: unique clinic IDs from role_assignments with scope.type == "clinic"
  const allowedClinics = useMemo(() => {
    const clinicIds = new Set();
    for (const ra of roleAssignments) {
      if (ra.scope?.type === 'clinic' && ra.scope?.id) {
        clinicIds.add(ra.scope.id);
      }
    }
    return [...clinicIds];
  }, [roleAssignments]);

  // Auto-select clinic if only one
  useEffect(() => {
    if (allowedClinics.length === 1 && activeClinicId !== allowedClinics[0]) {
      setActiveClinicIdRaw(allowedClinics[0]);
      sessionStorage.setItem(STORAGE_KEYS.activeClinic, allowedClinics[0]);
    }
  }, [allowedClinics, activeClinicId]);

  // Role checks
  //
  // MTMR-P0-CLOSURE — `hasRole` reflects the ACTIVE context, never the
  // union of the account's assignments. Holding provider_admin at
  // Provider B must not reveal Provider-admin features while the active
  // context is a Sales persona at Provider A. Workspace DISCOVERY still
  // uses `roleAssignments` (see `availableWorkspaces`) — that is a list
  // of legitimate choices, not an authority.
  //
  // Fallback: when the server context has not hydrated yet, a single
  // assignment is unambiguous (the server auto-selects exactly that one),
  // so it may be used. With several assignments and no confirmed context
  // we expose NOTHING — fail closed.
  const activeRoleId = useMemo(() => {
    if (activeContext?.role_id) return activeContext.role_id;
    if (roleAssignments.length === 1) return roleAssignments[0]?.role_id || null;
    return null;
  }, [activeContext, roleAssignments]);

  const hasRole = useCallback((roleId) => activeRoleId === roleId, [activeRoleId]);

  // Discovery-only helper (never an authorization input).
  const holdsRoleAnywhere = useCallback(
    (roleId) => roleAssignments.some(ra => ra.role_id === roleId),
    [roleAssignments]
  );

  const isSuperAdmin = useMemo(() => hasRole('treatx_super_admin'), [hasRole]);
  const isClinicAdmin = useMemo(() => hasRole('provider_admin'), [hasRole]);
  const isDoctor = useMemo(() => hasRole(MULTI_CLINIC_ROLE_ID), [hasRole]);
  const isAuthenticated = useMemo(() => permissions.length > 0 || roleAssignments.length > 0, [permissions, roleAssignments]);
  const canSwitchClinic = useMemo(() => allowedClinics.length > 1, [allowedClinics]);

  // Permission check
  const can = useCallback((permission) => {
    if (isSuperAdmin) return true;
    return permissions.includes(permission);
  }, [permissions, isSuperAdmin]);

  // Clinic context setter with validation
  const setActiveClinic = useCallback((clinicId) => {
    if (!allowedClinics.includes(clinicId)) {
      console.warn(`[RBAC] Clinic ${clinicId} not in allowedClinics:`, allowedClinics);
      return false;
    }
    setActiveClinicIdRaw(clinicId);
    sessionStorage.setItem(STORAGE_KEYS.activeClinic, clinicId);
    return true;
  }, [allowedClinics]);

  // OF-001B — derived workspace state.
  const availableWorkspaces = useMemo(
    () => (workspaces || []).filter(w => w.is_available && w.default_route),
    [workspaces]
  );
  const canSwitchWorkspace = useMemo(() => availableWorkspaces.length > 1, [availableWorkspaces]);
  const activeWorkspace = useMemo(() => {
    if (!activeWorkspaceId) return null;
    return availableWorkspaces.find(w => w.workspace_id === activeWorkspaceId) || null;
  }, [activeWorkspaceId, availableWorkspaces]);

  // OF-001F #2 — Single atomic workspace transaction (`switchWorkspace`).
  //
  // OWNER VALIDATION FAILURE 2 (real-browser): clicking the switcher
  // first sent the URL to `/clinic/home`, then bounced back to the
  // previous workspace's route, leaving the label stale.  The previous
  // pattern split the transaction into THREE separate side-effects:
  //   1. flushSync(state)  — RBACContext
  //   2. localStorage.setItem(activeWorkspace) — RBACContext
  //   3. navigate(default_route) — WorkspaceSwitcher (separate caller)
  //
  // Between step 1 and step 3 the React Router v7 navigator could
  // serialise its own dispatch, allowing a route guard reading
  // localStorage on the OLD path's render to redirect to `/clinic/home`
  // before the new URL took effect.
  //
  // The new contract eliminates the split.  ONE call ships the entire
  // transaction:
  //
  //   const ok = switchWorkspace(targetWorkspaceId);
  //
  // Internal order — strictly atomic (single JS task, no awaits):
  //
  //   a) Persist the new workspace id + clinic id to localStorage /
  //      sessionStorage FIRST.  Every non-React reader
  //      (`getActiveWorkspaceRole()`, `getActiveWorkspaceClinicId()`,
  //      `accessibleRoles` localStorage fallback, etc.) now answers
  //      with the new workspace BEFORE any React commit runs.
  //   b) `flushSync` the React state writes.  Every React subscriber
  //      (Header label, BootstrapGuard, RBACProtectedRoute,
  //      ClinicRoleRedirect, ClinicDataContext.clinicUser memo)
  //      sees the new workspace on its very next render.
  //   c) Mutate `window.location.hash` directly to the target route.
  //      HashRouter listens to `hashchange` and reconciles in the
  //      same tick.  This is the OWNER-MANDATED "one function, one
  //      route transaction" rule.  No `useNavigate` indirection —
  //      no opportunity for a parallel guard render to redirect to
  //      `/clinic/home`.
  //
  // OWNER REQUIREMENTS (verbatim):
  //   - `switchWorkspace` is the ONLY function the click handler calls.
  //   - During the transaction `/clinic/home` MUST NEVER appear in the
  //     URL.
  //   - No guard may redirect to `/clinic/home` during a valid
  //     transition.
  //   - No second click required.
  //
  // MTMR-P0 (2026-06) — THE SWITCH IS NOW SERVER-AUTHORITATIVE.
  //   0) `POST /api/auth/unified/context` re-mints the signed active
  //      workspace context (provider + role) server-side. If the server
  //      rejects (403 CONTEXT_NOT_PERMITTED / CONTEXT_REVOKED) NOTHING
  //      changes: no storage write, no React commit, no navigation, and
  //      the previously visible provider's data stays as it was.
  //   0b) On success every provider-scoped cache is purged and the page
  //      is re-mounted, so no Provider-A messaging / lead / patient /
  //      quote / task / financial state can remain visible under the
  //      Provider-B workspace, and the SSE stream is re-established only
  //      after the new context is confirmed.
  // TREATX-UAT-PROD-ADMISSION-RACE-01 — SINGLE-FLIGHT context proposal.
  // Nested admission boundaries (RBACProtectedRoute + WorkspaceBootstrapGuard)
  // and the bootstrap effect can legitimately reach the SAME unambiguous
  // decision in one commit; they must not produce two POSTs.
  const contextPostInFlight = useRef({ id: null, promise: null });
  const postContext = useCallback((workspaceId) => {
    const cur = contextPostInFlight.current;
    if (cur.id === workspaceId && cur.promise) return cur.promise;
    const promise = api.post('/auth/unified/context', { workspace_id: workspaceId })
      .then((res) => res?.data || null)
      .finally(() => {
        if (contextPostInFlight.current.id === workspaceId) {
          contextPostInFlight.current = { id: null, promise: null };
        }
      });
    contextPostInFlight.current = { id: workspaceId, promise };
    return promise;
  }, []);

  const switchWorkspace = useCallback(async (workspaceId) => {
    const target = (workspaces || []).find(w => w.workspace_id === workspaceId && w.is_available);
    if (!target || !target.default_route) {
      console.warn(`[RBAC/OF-001F-2] workspace_id ${workspaceId} not available or missing default_route`);
      // COM-FOUNDATION — record the failed transition for forensics.
      try {
        if (typeof window !== 'undefined' && window.__TREATX_RUNTIME__) {
          window.__TREATX_RUNTIME__.recordTransition({
            kind: 'switchWorkspace.rejected',
            requested_workspace_id: workspaceId,
            reason: target ? 'no-default-route' : 'workspace-not-available',
          });
        }
      } catch { /* ignore */ }
      return null;
    }
    // (0) SERVER-AUTHORITATIVE CONTEXT SWITCH — must succeed first.
    let _ctxResponse = null;
    try {
      _ctxResponse = await postContext(workspaceId);
    } catch (err) {
      const code = err?.response?.data?.detail?.code || err?.response?.data?.code;
      console.warn('[RBAC/MTMR-P0] context switch refused by server', code || err?.message);
      try {
        if (typeof window !== 'undefined' && window.__TREATX_RUNTIME__) {
          window.__TREATX_RUNTIME__.recordTransition({
            kind: 'switchWorkspace.rejected',
            requested_workspace_id: workspaceId,
            reason: code || 'server-refused',
          });
        }
      } catch { /* ignore */ }
      return null;
    }
    const t0 = (typeof performance !== 'undefined') ? performance.now() : 0;
    const previousWorkspaceId = activeWorkspaceId;
    const previousRoute = (typeof window !== 'undefined') ? (window.location.hash || '') : '';
    // (a) SSOT persistence — synchronous, BEFORE any React or
    //     navigation work.  Guards reading localStorage on any
    //     subsequent render see the new workspace.
    try {
      persistTrustedWorkspaceSelection(workspaceId);
      if (target.type === 'provider' && target.clinic_id) {
        sessionStorage.setItem(STORAGE_KEYS.activeClinic, target.clinic_id);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
      }
      // MTMR-P0 — purge provider-scoped caches so no previous-provider
      // messaging / lead / patient / quote / financial state survives.
      purgeProviderScopedState();
    } catch { /* private-mode storage failure is non-fatal */ }
    // (b) React state commit — atomic via flushSync.
    flushSync(() => {
      setActiveWorkspaceIdRaw(workspaceId);
      setContextRequired(false);
      // TREATX-UAT-PROD-ADMISSION-RACE-01 — the authoritative context
      // response is being applied in this same commit, so admission is
      // ready. A refused POST returned above and never reaches here.
      setProfileLoaded(true);
      if (_ctxResponse?.active_context) {
        setActiveContext(_ctxResponse.active_context);
        if (Array.isArray(_ctxResponse.permissions)) {
          setPermissions(_ctxResponse.permissions);
          try {
            localStorage.setItem(
              STORAGE_KEYS.permissions, JSON.stringify(_ctxResponse.permissions)
            );
          } catch { /* ignore */ }
        }
      }
      if (target.type === 'provider' && target.clinic_id) {
        setActiveClinicIdRaw(target.clinic_id);
      } else {
        setActiveClinicIdRaw(null);
      }
    });
    // (b2) MTMR-P0-CLOSURE — multi-tab safety. The signed context lives
    //      in ONE browser-wide cookie, so every other TreatX tab just
    //      had its server-side authority replaced. Tell them to drop
    //      Provider-sensitive state, close their streams and re-resolve
    //      the canonical context instead of continuing on stale data.
    notifyContextChanged(workspaceId);
    // (c) URL change — direct hash mutation.
    const targetHash = '#' + target.default_route;
    if (window.location.hash !== targetHash) {
      window.location.hash = targetHash;
    }
    // (d) MTMR-P0 — remount on the confirmed context. This closes the
    //     old SSE connection, drops every in-memory provider-scoped
    //     store (messaging, leads, cases, quotes, tasks, financials)
    //     and re-opens the stream only after the server-confirmed
    //     switch. No UI redesign; the route is already the target one.
    try {
      window.setTimeout(() => window.location.reload(), 0);
    } catch { /* ignore */ }
    // COM-FOUNDATION — record the completed transition for forensics.
    try {
      if (typeof window !== 'undefined' && window.__TREATX_RUNTIME__) {
        const t1 = (typeof performance !== 'undefined') ? performance.now() : 0;
        window.__TREATX_RUNTIME__.recordTransition({
          kind: 'switchWorkspace.committed',
          previous_workspace_id: previousWorkspaceId,
          requested_workspace_id: workspaceId,
          final_workspace_id: workspaceId,
          previous_route: previousRoute,
          target_route: target.default_route,
          duration_ms: Math.round(t1 - t0),
        });
      }
    } catch { /* ignore */ }
    return target;
  }, [workspaces, activeWorkspaceId, postContext]);

  // MTMR-P0-CLOSURE — `setActiveWorkspace` is NO LONGER a local-only
  // authority mutation. It previously made the UI believe the
  // Provider/Role authority had changed without any server context
  // switch, which is exactly the "local-only authority" defect. It now
  // delegates to the server-authoritative `switchWorkspace` transaction
  // (minus the navigation, which passive callers must not perform):
  // nothing changes unless `POST /auth/unified/context` succeeds.
  //
  // localStorage may only MIRROR server state for UX. It is never the
  // authority.
  const setActiveWorkspace = useCallback(async (workspaceId) => {
    const target = (workspaces || []).find(w => w.workspace_id === workspaceId && w.is_available);
    if (!target) {
      console.warn(`[RBAC/OF-001F-2] workspace_id ${workspaceId} not available (setActiveWorkspace)`);
      return null;
    }
    if (workspaceId === activeWorkspaceId && activeContext?.workspace_id === workspaceId) {
      return target;   // already the confirmed server context — no-op
    }
    let data = null;
    try {
      data = await postContext(workspaceId);
    } catch (err) {
      const code = err?.response?.data?.detail?.code || err?.response?.data?.code;
      console.warn('[RBAC/MTMR-P0] passive context sync refused by server', code || err?.message);
      return null;
    }
    try {
      persistTrustedWorkspaceSelection(workspaceId);
      if (target.type === 'provider' && target.clinic_id) {
        sessionStorage.setItem(STORAGE_KEYS.activeClinic, target.clinic_id);
      } else {
        sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
      }
      purgeProviderScopedState();
    } catch { /* ignore */ }
    flushSync(() => {
      setActiveWorkspaceIdRaw(workspaceId);
      setContextRequired(false);
      setProfileLoaded(true);   // authoritative context applied in this commit
      if (data?.active_context) setActiveContext(data.active_context);
      if (Array.isArray(data?.permissions)) {
        setPermissions(data.permissions);
        try {
          localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(data.permissions));
        } catch { /* ignore */ }
      }
      if (target.type === 'provider' && target.clinic_id) {
        setActiveClinicIdRaw(target.clinic_id);
      } else {
        setActiveClinicIdRaw(null);
      }
    });
    notifyContextChanged(workspaceId);
    return target;
  }, [workspaces, activeWorkspaceId, activeContext, postContext]);

  // Called after login to hydrate context
  const hydrateFromLogin = useCallback((perms, assignments, wss, opts = {}) => {
    signedOut.current = false;   // a new authoritative session begins here
    const p = perms || [];
    const ra = assignments || [];
    const ws = wss || [];
    setPermissions(p);
    setRoleAssignments(ra);
    setWorkspaces(ws);
    // MTMR-P0-CLOSURE — carry the server-validated context from the login
    // response. `requires_context_selection` means the account holds
    // several workspaces and the SERVER has NOT granted any Provider
    // authority yet: the UI must select one (server-validated) before any
    // Provider-scoped surface will answer.
    setActiveContext(opts.activeContext || null);
    setContextRequired(Boolean(opts.requiresContextSelection));
    if (opts.activeContext?.workspace_id) {
      setActiveWorkspaceIdRaw(opts.activeContext.workspace_id);
      persistTrustedWorkspaceSelection(opts.activeContext.workspace_id);
      // The ACTIVE clinic mirror must be written too, otherwise passive
      // route→workspace sync cannot tell Provider A's Sales workspace
      // from Provider B's (both own `/clinic/sales`) and would switch the
      // user away from the workspace they just chose.
      const ctxClinic = opts.activeContext.clinic_id || null;
      setActiveClinicIdRaw(ctxClinic);
      try {
        if (ctxClinic) sessionStorage.setItem(STORAGE_KEYS.activeClinic, ctxClinic);
        else sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
      } catch { /* ignore */ }
    }
    localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(p));
    localStorage.setItem(STORAGE_KEYS.roleAssignments, JSON.stringify(ra));
    localStorage.setItem(STORAGE_KEYS.workspaces, JSON.stringify(ws));
    // TREATX-UAT-PROD-ADMISSION-RACE-01 — LAST: the complete
    // authoritative login payload (permissions, assignments, workspaces,
    // activeContext, contextRequired, mirrors) has now been applied, so
    // protected admission may be evaluated. Never set before this point.
    setProfileLoaded(true);
  }, []);

  // Called on logout to clear everything
  const authEpoch = useRef(0);
  const signedOut = useRef(false);
  const clearRBAC = useCallback(() => {
    authEpoch.current += 1;   // invalidate any /me answer still in flight
    // TREATX-UAT-PROD-ADMISSION-RACE-01 — logout latch. The backend
    // logout call is fire-and-forget, so a `/me` issued right after the
    // local clear (e.g. by the `treatx:identity-changed` listener) can
    // still answer 200 on the not-yet-expired cookie and RE-HYDRATE the
    // session it was supposed to end — including re-persisting the
    // trusted selection. Nothing may reconcile until a new login.
    signedOut.current = true;
    setPermissions([]);
    setRoleAssignments([]);
    setWorkspaces([]);
    setActiveClinicIdRaw(null);
    setActiveWorkspaceIdRaw(null);
    setActiveContext(null);
    setContextRequired(false);
    setProfileLoaded(false);
    purgeProviderScopedState();
    localStorage.removeItem(STORAGE_KEYS.permissions);
    localStorage.removeItem(STORAGE_KEYS.roleAssignments);
    localStorage.removeItem(STORAGE_KEYS.workspaces);
    clearWorkspaceSelection();
    sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
  }, []);

  // Refresh profile from backend — syncs permissions + clinic memberships without re-login
  // FIND-H-08: auth transport is now the HttpOnly `treatx_auth` cookie
  // (JS cannot read it). Use the readable `treatx_csrf` cookie OR the
  // user snapshot as the presence indicator; the backend enforces
  // final validity via the cookie itself.
  const isLikelyAuthenticated = useCallback(() => {
    try {
      if (localStorage.getItem('treatx_unified_user')) return true;
      if (typeof document !== 'undefined' && (document.cookie || '').includes('treatx_csrf=')) return true;
    } catch { /* noop */ }
    return false;
  }, []);

  const refreshProfile = useCallback(async () => {
    if (signedOut.current) return null;
    if (refreshInFlight.current) return null;
    if (!isLikelyAuthenticated()) return null;

    refreshInFlight.current = true;
    const epoch = authEpoch.current;
    try {
      const res = await api.get(`/auth/unified/me`);
      // TREATX-UAT-PROD-ADMISSION-RACE-01 — the session was cleared while
      // this request was in flight (logout / context invalidation): its
      // payload is stale authority and must not be applied or persisted.
      if (epoch !== authEpoch.current) return null;
      if (res.status === 401) {
        // Token invalid → force logout
        clearRBAC();
        localStorage.removeItem('treatx_user');
        window.location.hash = '#/login';
        return null;
      }

      const data = res.data;
      const perms = data.permissions || [];
      const ra = data.role_assignments || [];
      const ws = data.workspaces || [];
      setPermissions(perms);
      setRoleAssignments(ra);
      setWorkspaces(ws);
      // MTMR-P0-CLOSURE/BATCH-A — the server reports the canonical state
      // (`context_state`: active | selection_required | revoked | none).
      // AVAILABLE workspaces (`workspaces`) are discovery only and are
      // NEVER treated as an active workspace.
      setActiveContext(data.active_context || null);
      setContextRequired(Boolean(
        data.context_selection_required
        ?? (!data.active_context && ra.length > 1)
      ));
      if (!data.active_context) {
        clearWorkspaceSelection();
        setActiveWorkspaceIdRaw(null);
        setActiveClinicIdRaw(null);
      }
      if (data.active_context?.workspace_id) {
        persistTrustedWorkspaceSelection(data.active_context.workspace_id);
        setActiveWorkspaceIdRaw(data.active_context.workspace_id);
        const ctxClinic = data.active_context.clinic_id || null;
        setActiveClinicIdRaw(ctxClinic);
        try {
          if (ctxClinic) sessionStorage.setItem(STORAGE_KEYS.activeClinic, ctxClinic);
          else sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
        } catch { /* ignore */ }
      }
      localStorage.setItem(STORAGE_KEYS.permissions, JSON.stringify(perms));
      localStorage.setItem(STORAGE_KEYS.roleAssignments, JSON.stringify(ra));
      localStorage.setItem(STORAGE_KEYS.workspaces, JSON.stringify(ws));
      setProfileLoaded(true);
      return data;
    } catch (err) {
      console.error('[RBAC] Profile refresh failed:', err);
      return null;
    } finally {
      refreshInFlight.current = false;
    }
  }, [clearRBAC, isLikelyAuthenticated]);

  // On mount: if we appear authenticated, refresh from backend to
  // sync latest permissions.
  const mountRefreshDone = useRef(false);
  useEffect(() => {
    if (mountRefreshDone.current) return;
    mountRefreshDone.current = true;
    if (isLikelyAuthenticated()) {
      refreshProfile();
      return;
    }
    // TREATX-UAT-PROD-ADMISSION-RACE-01 — no resumable session marker at
    // all: the cached RBAC snapshot can never become the current
    // authority (nothing will reconcile it), so purge it instead of
    // leaving admission permanently unresolved.
    clearRBAC();
  }, [refreshProfile, isLikelyAuthenticated, clearRBAC]);

  // OF-001B / OF-001E — initial workspace bootstrap.
  //
  // Selection rule (deterministic, runs ONLY when nothing is selected
  // yet — never overrides an explicit user choice):
  //   1. If the persisted `activeWorkspaceId` is still in
  //      `availableWorkspaces`, keep it.
  //   2. Otherwise — ONLY if activeWorkspaceId is null — pick the
  //      FIRST available workspace.
  //   3. If none is available, leave activeWorkspaceId = null.
  //
  // OF-001E #4 lock: this effect MUST NOT override a non-null
  // activeWorkspaceId.  The previous version did so when a freshly
  // returned `workspaces[]` reference temporarily didn't include the
  // user's selection — that race produced the "blank page on first
  // click" symptom.  Drift-guarded by
  // `test_rbac_auto_pick_never_overrides_explicit_selection`.
  // MTMR-P0-CLOSURE — initial workspace bootstrap is SERVER-CONFIRMED.
  //
  // The previous version selected the first available workspace in
  // localStorage/React only, i.e. the UI believed a Provider authority
  // had been chosen while the server had none. It now performs the real
  // context switch (`setActiveWorkspace` → POST /auth/unified/context)
  // and commits nothing if the server refuses.
  //
  // It still NEVER overrides a confirmed selection (OF-001E #4 lock).
  const bootstrapInFlight = useRef(false);
  useEffect(() => {
    if (!availableWorkspaces || availableWorkspaces.length === 0) return;
    // TREATX-UAT-PROD-ADMISSION-RACE-01 — never propose a context from
    // pre-reconciliation cached state. Same gate as the guard's recovery
    // effect (Codex solution spec): readiness first, then selection.
    if (!profileLoaded) return;
    // The server already confirmed a context that is still available.
    if (activeContext?.workspace_id
      && availableWorkspaces.some(w => w.workspace_id === activeContext.workspace_id)) {
      return;
    }
    // A local selection exists and the server has not contradicted it yet.
    if (activeWorkspaceId
      && !contextRequired
      && availableWorkspaces.some(w => w.workspace_id === activeWorkspaceId)) {
      return;
    }
    if (bootstrapInFlight.current) return;
    // BATCH-A: NEVER infer intent from assignment order. Only an
    // explicit previous selection may be restored, or the single
    // available workspace (unambiguous). Otherwise the app stays in the
    // context-selection state and the user chooses.
    let next = null;
    try {
      const lastId = readTrustedWorkspaceSelection();
      if (lastId) next = availableWorkspaces.find(w => w.workspace_id === lastId) || null;
    } catch { /* ignore */ }
    // TREATX-UAT-PROD-CONTEXT-SURFACE-01c (Codex re-review finding).
    // The previous `availableWorkspaces.length === 1` rule equated ONE
    // CATALOGUE ENTRY with ONE POSSIBLE CONTEXT. The canonical catalogue
    // does not publish a route for every assignment (`treatx_*` global
    // roles among them), so a mixed-persona account (e.g. Provider +
    // TreatX Admin) could have its Provider context minted silently —
    // including while a NON-provider protected route was being entered.
    // Automatic selection now uses the ONE shared rule; when it cannot
    // be proven, the context stays unselected and the explicit
    // selection/recovery surface is shown instead.
    if (!next) next = resolveUnambiguousWorkspace(roleAssignments, workspaces);
    if (!next) return;
    bootstrapInFlight.current = true;
    Promise.resolve(setActiveWorkspace(next.workspace_id))
      .finally(() => { bootstrapInFlight.current = false; });
  }, [availableWorkspaces, workspaces, roleAssignments, activeWorkspaceId,
      activeContext, contextRequired, profileLoaded, setActiveWorkspace]);

  // OF-001C / OF-001E — `treatx:identity-changed` listener.
  //
  // The event is dispatched ONLY by:
  //   - login (unifiedAuth + UnifiedLoginPage)
  //   - logout (AuthContext)
  //   - invite accept (PendingInvitationsBanner)
  //
  // Workspace SWITCH does NOT dispatch this event (OF-001E §4):
  // workspaces[] is unchanged by a switch, so refetching /me only
  // races the transition.  Drift-guarded by
  // `test_workspace_switch_does_not_dispatch_identity_changed`.
  useEffect(() => {
    const onIdentityChanged = () => { refreshProfile(); };
    window.addEventListener('treatx:identity-changed', onIdentityChanged);
    return () => window.removeEventListener('treatx:identity-changed', onIdentityChanged);
  }, [refreshProfile]);

  // MTMR-P0 — minimum cross-tab reconciliation. The signed active
  // context lives in one browser-wide cookie, so a switch in another tab
  // changes THIS tab's server-side authorization too. On focus we ask
  // the server which workspace is active and, on divergence, purge the
  // provider-scoped caches and remount on the server's context rather
  // than rendering another provider's data.
  useEffect(() => {
    const reconcile = async () => {
      if (document.visibilityState === 'hidden') return;
      // P0-PROVIDER-ONBOARDING-RECOVERY-01 (Failure B) — `treatx_csrf` is a
      // SHARED cookie: the application Status Portal and the onboarding
      // wizard mint it for their own application-bound sessions, so
      // `isLikelyAuthenticated()` reads TRUE for a provider who never had an
      // account. Probing RBAC there produced a guaranteed 401 on EVERY window
      // focus — including the focus event fired when the native file-picker
      // closes. Those surfaces own their own session; never probe RBAC there.
      if (isSelfOwnedSessionRoute()) return;
      if (!isLikelyAuthenticated()) return;
      try {
        const res = await api.get('/auth/unified/me');
        const serverWs = res?.data?.active_context?.workspace_id;
        const localWs = readTrustedWorkspaceSelection();
        if (serverWs && localWs && serverWs !== localWs) {
          persistTrustedWorkspaceSelection(serverWs);
          const clinicId = res.data.active_context.clinic_id;
          if (clinicId) sessionStorage.setItem(STORAGE_KEYS.activeClinic, clinicId);
          else sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
          purgeProviderScopedState();
          window.location.reload();
        }
      } catch { /* offline / 401 handled elsewhere */ }
    };
    window.addEventListener('focus', reconcile);
    document.addEventListener('visibilitychange', reconcile);
    return () => {
      window.removeEventListener('focus', reconcile);
      document.removeEventListener('visibilitychange', reconcile);
    };
  }, [isLikelyAuthenticated]);

  // MTMR-P0-CLOSURE — ONE central handler for canonical context failures
  // (`treatx:context-invalid`, dispatched by the api.js interceptor for
  // 403 CONTEXT_REVOKED / CONTEXT_REQUIRED / CONTEXT_NOT_PERMITTED).
  //
  //   - stop Provider-scoped requests (activeWorkspaceId/context cleared
  //     ⇒ the provider shells render the workspace-selection surface)
  //   - clear Provider-sensitive cached state
  //   - close SSE / WebSocket / live messaging (remount via the
  //     `treatx:realtime-close` signal + provider cache purge)
  //   - refresh the canonical workspace availability from the server
  //   - NEVER silently choose another Provider
  //   - Patient/global state is untouched when it remains legitimate.
  const contextInvalidHandling = useRef(false);
  useEffect(() => {
    const onContextInvalid = async (evt) => {
      if (contextInvalidHandling.current) return;
      contextInvalidHandling.current = true;
      try {
        purgeProviderScopedState();
        try { window.dispatchEvent(new CustomEvent('treatx:realtime-close')); } catch { /* noop */ }
        flushSync(() => {
          // TREATX-UAT-PROD-ADMISSION-RACE-01 — (1) readiness false and
          // (2) protected admission removal happen in the SAME commit as
          // the context invalidation, so no protected child survives on
          // the now-stale authority while /me reconciles.
          setProfileLoaded(false);
          setActiveContext(null);
          setActiveWorkspaceIdRaw(null);
          setActiveClinicIdRaw(null);
          setPermissions([]);
          setContextRequired(true);
        });
        try {
          clearWorkspaceSelection();
          localStorage.removeItem(STORAGE_KEYS.permissions);
          sessionStorage.removeItem(STORAGE_KEYS.activeClinic);
        } catch { /* noop */ }
        const code = evt?.detail?.code;
        if (code === 'CONTEXT_REVOKED') {
          import('sonner').then(({ toast }) => {
            try {
              toast.error('Your access to that workspace changed. Please select a workspace.');
            } catch { /* noop */ }
          }).catch(() => { /* noop */ });
        }
        // Re-resolve what the account may legitimately choose NOW.
        await refreshProfile();
      } finally {
        setTimeout(() => { contextInvalidHandling.current = false; }, 1000);
      }
    };
    window.addEventListener('treatx:context-invalid', onContextInvalid);
    return () => window.removeEventListener('treatx:context-invalid', onContextInvalid);
  }, [refreshProfile]);

  // MTMR-P0-CLOSURE — cross-tab context replacement. One tab switching
  // Provider replaces the browser-wide cookie, so this tab's server-side
  // authority already changed: drop Provider-sensitive state, close
  // streams and re-resolve the canonical context before continuing.
  useEffect(() => {
    const onRemoteContextChange = (payload) => {
      const localWs = readTrustedWorkspaceSelection();
      if (payload?.workspace_id && payload.workspace_id === localWs) return;
      // TREATX-UAT-PROD-ADMISSION-RACE-01 — this tab's server-side
      // authority was just replaced: drop admission readiness before the
      // reload re-resolves it against /me.
      setProfileLoaded(false);
      try { window.dispatchEvent(new CustomEvent('treatx:realtime-close')); } catch { /* noop */ }
      purgeProviderScopedState();
      try {
        if (payload?.workspace_id) {
          persistTrustedWorkspaceSelection(payload.workspace_id);
        }
      } catch { /* noop */ }
      // Re-resolve against the server (never trust the broadcast itself).
      try { window.location.reload(); } catch { /* noop */ }
    };
    let ch = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        ch = new BroadcastChannel(CONTEXT_CHANNEL);
        ch.onmessage = (e) => onRemoteContextChange(e?.data);
      }
    } catch { /* noop */ }
    const onStorage = (e) => {
      if (e.key !== CONTEXT_PING_KEY || !e.newValue) return;
      try { onRemoteContextChange(JSON.parse(e.newValue)); } catch { /* noop */ }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      try { if (ch) ch.close(); } catch { /* noop */ }
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const value = useMemo(() => ({
    permissions,
    roleAssignments,
    allowedClinics,
    activeClinicId,
    isAuthenticated,
    isSuperAdmin,
    isClinicAdmin,
    isDoctor,
    canSwitchClinic,
    profileLoaded,
    can,
    hasRole,
    holdsRoleAnywhere,
    activeContext,
    activeRoleId,
    contextRequired,
    setActiveClinic,
    hydrateFromLogin,
    clearRBAC,
    refreshProfile,
    // OF-001B
    workspaces,
    availableWorkspaces,
    activeWorkspace,
    activeWorkspaceId,
    canSwitchWorkspace,
    setActiveWorkspace,
    switchWorkspace,
  }), [
    permissions, roleAssignments, allowedClinics, activeClinicId,
    isAuthenticated, isSuperAdmin, isClinicAdmin, isDoctor, canSwitchClinic,
    profileLoaded, can, hasRole, holdsRoleAnywhere, activeContext, activeRoleId, contextRequired,
    setActiveClinic, hydrateFromLogin, clearRBAC, refreshProfile,
    workspaces, availableWorkspaces, activeWorkspace, activeWorkspaceId, canSwitchWorkspace, setActiveWorkspace, switchWorkspace,
  ]);

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
};

export const useRBAC = () => {
  const ctx = useContext(RBACContext);
  if (!ctx) throw new Error('useRBAC must be used within RBACProvider');
  return ctx;
};

export default RBACContext;
